import { useEffect, useRef, useState } from 'react';
import { subscribeToChanges } from './revalidation';

/**
 * Consulta que se refaz quando algo muda.
 *
 * **Era outra coisa até a Fase 12**, e a diferença merece ser lida antes de mexer aqui. A
 * implementação anterior era o `useLiveQuery` do Dexie: ele observava as tabelas lidas
 * durante o callback — inclusive lá no fundo, dentro do repositório — e atualizava a tela
 * sozinho, sem que ninguém precisasse avisar. O banco era o cache, e ele era reativo
 * (ADR-103).
 *
 * Com os dados no servidor não há o que observar: a leitura é uma requisição, e ela termina.
 * A assinatura continua a mesma de propósito — as trinta e sete chamadas espalhadas pelas
 * telas não mudam uma linha —, mas o que dispara a rebusca passa a ser explícito:
 *
 * - a **primeira montagem**;
 * - uma mudança nas **dependências** — trocar de obra, de semana, de dia;
 * - um **aviso de escrita**, publicado pelo `useCommand` e ouvido também entre abas
 *   (`revalidation.ts`).
 *
 * `undefined` continua significando "ainda não sei", e não "vazio". É o que permite à tela
 * distinguir o esqueleto de carregamento de uma lista genuinamente sem itens — contrato que
 * já existia e que muitas telas leem.
 *
 * ## O que se perdeu, e onde ele aparece
 *
 * A gravação de outra pessoa não chega mais sozinha. Quem está com a tela aberta só a vê
 * ao navegar ou ao agir. É a regressão assumida junto com a escolha do servidor, e o lugar
 * de resolvê-la é o canal de aviso que a §Reatividade do plano deixou em aberto — não este
 * arquivo, que já está pronto para uma segunda origem de aviso.
 */
export function useLive<T>(querier: () => Promise<T>, deps: unknown[] = []): T | undefined {
  const [valor, setValor] = useState<T | undefined>(undefined);

  // A consulta muda a cada render (é uma closure nova), e não pode entrar nas dependências
  // do efeito: entraria em laço. Quem decide quando refazer é `deps`, como sempre foi.
  const consultar = useRef(querier);
  consultar.current = querier;

  useEffect(() => {
    let vivo = true;

    const buscar = (): void => {
      void consultar.current().then(
        (resultado) => {
          // Duas respostas fora de ordem — a segunda mais rápida que a primeira — deixariam
          // a tela com o resultado da consulta antiga. O componente desmontado ou com
          // dependências novas simplesmente descarta o que chegou atrasado.
          if (vivo) setValor(resultado);
        },
        () => {
          // Falha de leitura não derruba a árvore: o `Result` dos casos de uso já carrega
          // a recusa, e o que chega aqui como exceção é do transporte, que a própria
          // consulta converteu. Voltar a `undefined` mostra o carregamento de novo, que é
          // melhor do que congelar um dado que pode ter deixado de valer.
          if (vivo) setValor(undefined);
        },
      );
    };

    buscar();
    const cancelar = subscribeToChanges(buscar);

    return () => {
      vivo = false;
      cancelar();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `deps` é o contrato desta API, como no `useLiveQuery` que ela substituiu
  }, deps);

  return valor;
}
